<template>
  <div class="editor">
    <slot />
    <br>
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
      <div class="menubar">
        <button
          v-if="bold"
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() }"
          @click="commands.bold"
        >
          <format-bold-icon />
        </button>

        <button
          v-if="italic"
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() }"
          @click="commands.italic"
        >
          <format-italic-icon />
        </button>

        <button
          v-if="strikethrough"
          class="menubar__button"
          :class="{ 'is-active': isActive.strike() }"
          @click="commands.strike"
        >
          <format-strikethrough-icon />
        </button>

        <button
          v-if="underline"
          class="menubar__button"
          :class="{ 'is-active': isActive.underline() }"
          @click="commands.underline"
        >
          <format-underline-icon />
        </button>

        <button
          v-if="code"
          class="menubar__button"
          :class="{ 'is-active': isActive.code() }"
          @click="commands.code"
        >
          <code-tags-icon />
        </button>

        <button
          v-if="paragraph"
          class="menubar__button"
          :class="{ 'is-active': isActive.paragraph() }"
          @click="commands.paragraph"
        >
          <format-paragraph-icon />
        </button>

        <button
          v-if="headings"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click="commands.heading({ level: 1 })"
        >
          <format-header1-icon />
        </button>

        <button
          v-if="headings"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click="commands.heading({ level: 2 })"
        >
          <format-header2-icon />
        </button>

        <button
          v-if="headings"
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click="commands.heading({ level: 3 })"
        >
          <format-header3-icon />
        </button>

        <button
          v-if="bulletedList"
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click="commands.bullet_list"
        >
          <format-list-bulleted-icon />
        </button>

        <button
          v-if="numberedList"
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click="commands.ordered_list"
        >
          <format-list-numbered-icon />
        </button>

        <button
          v-if="quote"
          class="menubar__button"
          :class="{ 'is-active': isActive.blockquote() }"
          @click="commands.blockquote"
        >
          <format-quote-close-icon />
        </button>

        <button
          v-if="horizontalRule"
          class="menubar__button"
          @click="commands.horizontal_rule"
        >
          <minus-icon />
        </button>

        <button
          v-if="history"
          class="menubar__button"
          @click="commands.undo"
        >
          <undo-icon />
        </button>

        <button
          v-if="history"
          class="menubar__button"
          @click="commands.redo"
        >
          <redo-icon />
        </button>
      </div>
    </editor-menu-bar>
    <hr v-if="hasMenuBar">
    <div class="editor-scroller">
      <editor-content
        class="editor__content"
        :editor="editor"
      />
      <div v-if="needsBottomPadding" style="padding-bottom: 100px" />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from 'tiptap';
import {
    Blockquote,
    CodeBlock,
    HardBreak,
    Heading,
    HorizontalRule,
    OrderedList,
    BulletList,
    ListItem,
    TodoItem,
    TodoList,
    Bold,
    Code,
    Italic,
    Link,
    Strike,
    Underline,
    History,
    Image,
    Placeholder,
} from 'tiptap-extensions';

import FormatBoldIcon from 'vue-material-design-icons/FormatBold.vue';
import FormatItalicIcon from 'vue-material-design-icons/FormatItalic.vue';
import FormatStrikethroughIcon from 'vue-material-design-icons/FormatStrikethrough.vue';
import FormatUnderlineIcon from 'vue-material-design-icons/FormatUnderline.vue';
import CodeTagsIcon from 'vue-material-design-icons/CodeTags.vue';
import FormatParagraphIcon from 'vue-material-design-icons/FormatParagraph.vue';
import FormatHeader1Icon from 'vue-material-design-icons/FormatHeader1.vue';
import FormatHeader2Icon from 'vue-material-design-icons/FormatHeader2.vue';
import FormatHeader3Icon from 'vue-material-design-icons/FormatHeader3.vue';
import FormatListBulletedIcon from 'vue-material-design-icons/FormatListBulleted.vue';
import FormatListNumberedIcon from 'vue-material-design-icons/FormatListNumbered.vue';
import FormatQuoteCloseIcon from 'vue-material-design-icons/FormatQuoteClose.vue';
import MinusIcon from 'vue-material-design-icons/Minus.vue';
import UndoIcon from 'vue-material-design-icons/Undo.vue';
import RedoIcon from 'vue-material-design-icons/Redo.vue';
import Iframe from '../IFrame.js';

export default {
    // eslint-disable-next-line vue/name-property-casing
    name: 'r-rich-text-editor',
    components: {
        EditorContent,
        EditorMenuBar,
        [FormatBoldIcon.name]: FormatBoldIcon,
        [FormatItalicIcon.name]: FormatItalicIcon,
        [FormatStrikethroughIcon.name]: FormatStrikethroughIcon,
        [FormatUnderlineIcon.name]: FormatUnderlineIcon,
        [CodeTagsIcon.name]: CodeTagsIcon,
        [FormatParagraphIcon.name]: FormatParagraphIcon,
        [FormatHeader1Icon.name]: FormatHeader1Icon,
        [FormatHeader2Icon.name]: FormatHeader2Icon,
        [FormatHeader3Icon.name]: FormatHeader3Icon,
        [FormatListBulletedIcon.name]: FormatListBulletedIcon,
        [FormatListNumberedIcon.name]: FormatListNumberedIcon,
        [FormatQuoteCloseIcon.name]: FormatQuoteCloseIcon,
        [MinusIcon.name]: MinusIcon,
        [UndoIcon.name]: UndoIcon,
        [RedoIcon.name]: RedoIcon,
    },
    data() {
        return {
            typingTimer: null, // timer identifier
            doneTypingInterval: 3000, // time in ms for the user to finish typing before firing event
            isInitialized: false,
            editor: new Editor({
                editable: this.isEditable,
                extensions: [
                    new Blockquote(),
                    new BulletList(),
                    new CodeBlock(),
                    new HardBreak(),
                    new Heading({ levels: [1, 2, 3] }),
                    new HorizontalRule(),
                    new ListItem(),
                    new OrderedList(),
                    new TodoItem(),
                    new TodoList(),
                    new Link(),
                    new Bold(),
                    new Code(),
                    new Italic(),
                    new Strike(),
                    new Underline(),
                    new History(),
                    new Iframe(),
                    new Image(),
                    new Placeholder({
                        emptyEditorClass: 'is-editor-empty',
                        emptyNodeClass: 'is-empty',
                        emptyNodeText: 'Click here to begin writing…',
                        showOnlyWhenEditable: true,
                        showOnlyCurrent: true,
                    }),
                ],
                content: 'Loading...',
                onUpdate: this.editorUpdated,
            })
        };
    },
    methods: {
        editorUpdated() {
            clearTimeout(this.typingTimer);
            this.typingTimer = setTimeout(this.emitUpdateEvent, this.doneTypingInterval);
        },
        emitUpdateEvent() {
            this.$emit('onUpdate', this.editor.getHTML());
        }
    },
    props: {
        content: {
            type: String,
            default: '',
            required: true,
        },
        isEditable: {
            type: Boolean,
            default: true,
        },
        bold: {
            type: Boolean,
            default: false,
        },
        italic: {
            type: Boolean,
            default: false,
        },
        strikethrough: {
            type: Boolean,
            default: false,
        },
        underline: {
            type: Boolean,
            default: false,
        },
        code: {
            type: Boolean,
            default: false,
        },
        paragraph: {
            type: Boolean,
            default: false,
        },
        headings: {
            type: Boolean,
            default: false,
        },
        bulletedList: {
            type: Boolean,
            default: false,
        },
        numberedList: {
            type: Boolean,
            default: false,
        },
        quote: {
            type: Boolean,
            default: false,
        },
        horizontalRule: {
            type: Boolean,
            default: false,
        },
        history: {
            type: Boolean,
            default: false,
        },
        needsBottomPadding: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        hasMenuBar() {
            return this.isEditable && (this.bold
              || this.italic
              || this.strikethrough
              || this.underline
              || this.code
              || this.paragraph
              || this.headings
              || this.bulletedList
              || this.numberedList
              || this.quote
              || this.horizontalRule
              || this.history
            );
        }
    },
    watch: {
        isEditable() {
            this.editor.setOptions({
                editable: this.isEditable,
            });
        },
        content() {
            if (!this.isInitialized) {
                this.editor.setContent(this.content);
                this.isInitialized = true;
            }
        }
    },
    beforeDestroy() {
        this.isInitialized = false;
        this.editor.destroy();
    },
};
</script>

<style lang="scss">
.editor p.is-editor-empty:first-child::before {
  content: attr(data-empty-text);
  float: left;
  color: #aaa;
  pointer-events: none;
  height: 0;
  font-style: italic;
}
</style>
